import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HardDrive, Plus, Save, Trash2, FolderCog, ChevronUp, ChevronDown, Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Section, Progress } from "@/components/shared";
import { DriveImportWizard } from "@/components/catalog/DriveImportWizard";

const DEFAULT_IMAGE_CATS = [
  { id: "img-white-background", name: "White Background", drive_folder: "White Background" },
  { id: "img-carousel-lifestyle", name: "Carousel / Lifestyle", drive_folder: "Carousel & Lifestyle" },
  { id: "img-graphics", name: "Graphics", drive_folder: "Graphics" },
];
const DEFAULT_VIDEO_CATS = [
  { id: "vid-product-video", name: "Product Video", drive_folder: "Product Video" },
  { id: "vid-reels-shorts", name: "Reels / Shorts", drive_folder: "Reels & Shorts" },
  { id: "vid-rpd", name: "RPD", drive_folder: "RPD" },
  { id: "vid-raw-footage", name: "Raw Footage", drive_folder: "Raw Footage" },
];

function CategoryEditor({ clientId, kind, title, blurb, endpoint, defaults, testPrefix }) {
  const [cats, setCats] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/clients/${clientId}/${endpoint}`).then((r) => setCats(r.data.categories || [])).catch((e) => toast.error(errMsg(e)));
  }, [clientId, endpoint]);
  if (cats === null) return <div className="text-sm text-slate-500">Loading {kind} categories…</div>;
  const update = (index, patch) => setCats((cur) => cur.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  const add = () => setCats((cur) => [...cur, { name: "", drive_folder: "" }]);
  const remove = (index) => setCats((cur) => cur.filter((_, i) => i !== index));
  const move = (index, delta) => setCats((cur) => {
    const next = [...cur]; const target = index + delta;
    if (target < 0 || target >= next.length) return cur;
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const save = async () => {
    const clean = cats.map((c) => ({ id: c.id, name: (c.name || "").trim(), drive_folder: (c.drive_folder || "").trim() || (c.name || "").trim().replace(/\//g, "&") })).filter((c) => c.name);
    if (!clean.length) { toast.error(`Keep at least one ${kind} category`); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/clients/${clientId}/${endpoint}`, { categories: clean });
      setCats(data.categories);
      toast.success(`${title} saved`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Section title={title} testId={`${testPrefix}-settings`}>
      <p className="text-sm text-slate-500 mb-4">{blurb}</p>
      <div className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><span>On-screen name</span><span>Drive folder name</span><span /></div>
      <div className="space-y-2" data-testid={`${testPrefix}-list`}>
        {cats.map((cat, index) => (
          <div className="grid grid-cols-[1fr_1fr_auto] gap-2" key={cat.id || index}>
            <Input value={cat.name} placeholder="e.g. Product Video" onChange={(e) => update(index, { name: e.target.value })} data-testid={`${testPrefix}-name-${index}`} />
            <Input value={cat.drive_folder} placeholder="Drive folder" onChange={(e) => update(index, { drive_folder: e.target.value })} data-testid={`${testPrefix}-folder-${index}`} />
            <div className="flex items-center gap-0.5">
              <Button type="button" size="icon" variant="ghost" className="h-8 w-7" onClick={() => move(index, -1)} data-testid={`${testPrefix}-up-${index}`}><ChevronUp size={14} /></Button>
              <Button type="button" size="icon" variant="ghost" className="h-8 w-7" onClick={() => move(index, 1)} data-testid={`${testPrefix}-down-${index}`}><ChevronDown size={14} /></Button>
              <Button type="button" size="icon" variant="ghost" className="h-8 w-7" onClick={() => remove(index)} data-testid={`${testPrefix}-remove-${index}`}><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid={`${testPrefix}-add-btn`}><Plus size={13} className="mr-1" />Add category</Button>
        <Button type="button" size="sm" variant="ghost" onClick={() => setCats(defaults)} data-testid={`${testPrefix}-reset-btn`}>Reset to default</Button>
        <Button type="button" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={save} data-testid={`${testPrefix}-save-btn`}><Save size={13} className="mr-1" />{busy ? "Saving…" : "Save categories"}</Button>
      </div>
    </Section>
  );
}

export function ClientDriveSettings({ clientId }) {
  const [job, setJob] = useState(null);
  const [cleanup, setCleanup] = useState(null);

  useEffect(() => {
    api.get(`/clients/${clientId}/product-drive-repair`).then((r) => setJob(r.data)).catch(() => {});
    api.get(`/clients/${clientId}/image-cleanup`).then((r) => setCleanup(r.data)).catch(() => {});
  }, [clientId]);

  useEffect(() => {
    const active = [job, cleanup].some((j) => j && ["queued", "running"].includes(j.status));
    if (!active) return undefined;
    const timer = window.setTimeout(async () => {
      try { const { data } = await api.get(`/clients/${clientId}/product-drive-repair`); setJob(data); } catch { /* ignore */ }
      try { const { data } = await api.get(`/clients/${clientId}/image-cleanup`); setCleanup(data); } catch { /* ignore */ }
    }, 1600);
    return () => window.clearTimeout(timer);
  }, [job, cleanup, clientId]);

  const prepare = async () => {
    try { const { data } = await api.post(`/clients/${clientId}/product-drive-repair`); setJob(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const runCleanup = async () => {
    try { const { data } = await api.post(`/clients/${clientId}/image-cleanup`); setCleanup(data); toast.success("Cleanup started"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const pct = job?.total ? Math.round((job.processed / job.total) * 100) : 0;
  const cpct = cleanup?.total ? Math.round((cleanup.processed / cleanup.total) * 100) : 0;
  const jobRunning = job && ["queued", "running"].includes(job.status);
  const cleanupRunning = cleanup && ["queued", "running"].includes(cleanup.status);

  return (
    <div className="space-y-6" data-testid="client-drive-settings">
      <DriveImportWizard clientId={clientId} />
      <CategoryEditor clientId={clientId} kind="image" title="Image categories" endpoint="image-categories" defaults={DEFAULT_IMAGE_CATS} testPrefix="image-category"
        blurb="These categories are the image filters on every product and the Drive sub-folders created inside each colour (and _All colours). _Uncategorised and Video are always present. The on-screen name can differ from the Drive folder name." />
      <CategoryEditor clientId={clientId} kind="video" title="Video categories" endpoint="video-categories" defaults={DEFAULT_VIDEO_CATS} testPrefix="video-category"
        blurb="These categories are the video filters on every product and the Drive sub-folders created inside each colour's Video folder. _Uncategorised is always present. The on-screen name can differ from the Drive folder name." />

      <Section title="Apply to Drive" testId="prepare-drive-section">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 shrink-0 rounded-md bg-slate-900 text-white grid place-items-center"><HardDrive size={18} /></div>
          <div className="flex-1">
            <p className="text-sm text-slate-600">Create missing folders, rename in place and tidy every product for this client. Safe to run more than once.</p>
            <Button className="mt-3 bg-slate-900 hover:bg-slate-800" onClick={prepare} disabled={jobRunning} data-testid="prepare-drive-folders-settings-btn"><FolderCog size={15} className="mr-1.5" />{jobRunning ? "Preparing…" : "Prepare Drive folders"}</Button>
            {job && job.status !== "idle" && (
              <div className="mt-3 text-xs text-slate-600" data-testid="prepare-drive-progress">
                <div className="flex items-center justify-between mb-1"><span className="font-medium capitalize">{job.status}</span>{job.total ? <span>{job.processed}/{job.total}</span> : null}</div>
                {jobRunning && <Progress value={pct} />}
                {job.status === "completed" && <span>Done · {job.created || 0} created · {job.renamed || 0} renamed · {job.shares_removed || 0} client shares removed</span>}
                {job.error && <span className="text-rose-700">{job.error}</span>}
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section title="Clean up existing files" testId="image-cleanup-section">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 shrink-0 rounded-md bg-amber-500 text-white grid place-items-center"><Sparkles size={18} /></div>
          <div className="flex-1">
            <p className="text-sm text-slate-600">Re-map older image and video categories to your current ones, move each Drive file into its matching colour + category folder, merge duplicate folders and archive emptied ones. No file is ever deleted. Run once per client.</p>
            <Button className="mt-3 bg-amber-600 hover:bg-amber-700 text-white" onClick={runCleanup} disabled={cleanupRunning} data-testid="run-image-cleanup-btn"><Sparkles size={15} className="mr-1.5" />{cleanupRunning ? "Cleaning up…" : "Clean up existing files"}</Button>
            {cleanup && cleanup.status !== "idle" && (
              <div className="mt-3 text-xs text-slate-600" data-testid="image-cleanup-progress">
                <div className="flex items-center justify-between mb-1"><span className="font-medium capitalize">{cleanup.status}</span>{cleanup.total ? <span>{cleanup.processed}/{cleanup.total}</span> : null}</div>
                {cleanupRunning && <Progress value={cpct} />}
                {cleanup.status === "completed" && <span>Done · {cleanup.remapped || 0} re-categorised · {cleanup.moved || 0} files moved</span>}
                {cleanup.error && <span className="text-rose-700">{cleanup.error}</span>}
              </div>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
